import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { CloseButton } from './closeButton.tsx';

const VIEW_PAD_PX = 8;
const GAP_PX = 8;

export type FloatAnchor = Pick<DOMRect, 'left' | 'top' | 'right' | 'width' | 'height'>;

type FloatBox = { left: number; top: number };

const positions = new Map<string, FloatBox>();
const openIds = new Set<string>();

export function isFloatingPanelOpen(): boolean {
  return openIds.size > 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampFloatBox(box: FloatBox, width: number, height: number): FloatBox {
  const maxLeft = window.innerWidth - width - VIEW_PAD_PX;
  const maxTop = window.innerHeight - height - VIEW_PAD_PX;

  return {
    left: clamp(box.left, VIEW_PAD_PX, Math.max(VIEW_PAD_PX, maxLeft)),
    top: clamp(box.top, VIEW_PAD_PX, Math.max(VIEW_PAD_PX, maxTop)),
  };
}

function placeFloat(anchor: FloatAnchor, width: number, height: number): FloatBox {
  return clampFloatBox({ left: anchor.right + GAP_PX, top: anchor.top }, width, height);
}

function centerFloat(width: number, height: number): FloatBox {
  return clampFloatBox(
    {
      left: (window.innerWidth - width) / 2,
      top: (window.innerHeight - height) / 2,
    },
    width,
    height,
  );
}

export function FloatingPanel({
  id,
  title,
  anchor,
  onDismiss,
  onSubmit,
  children,
  footer,
  class: className,
}: {
  id: string;
  title: string;
  anchor?: FloatAnchor;
  onDismiss: () => void;
  /** Enter commits like Done. Escape still calls `onDismiss`. */
  onSubmit?: () => void;
  children: ComponentChildren;
  footer?: ComponentChildren;
  class?: string;
}): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<FloatBox | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragged = useRef(false);
  const [box, setBox] = useState<FloatBox | null>(null);
  const [dragging, setDragging] = useState(false);

  boxRef.current = box;

  useLayoutEffect(() => {
    const node = panelRef.current;

    if (!node) {
      return;
    }

    const stored = positions.get(id);
    const next = stored
      ? clampFloatBox(stored, node.offsetWidth, node.offsetHeight)
      : anchor
        ? placeFloat(anchor, node.offsetWidth, node.offsetHeight)
        : centerFloat(node.offsetWidth, node.offsetHeight);

    boxRef.current = next;
    setBox(next);
  }, [id, anchor]);

  useEffect(() => {
    openIds.add(id);

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onDismiss();
        return;
      }

      if (event.key !== 'Enter' || !onSubmit || event.repeat) {
        return;
      }

      const active = document.activeElement;

      // Multiline fields keep Enter for a newline; everything else is Done.
      if (active instanceof HTMLTextAreaElement) {
        return;
      }

      // Flush a focused text field (hex) before commit — blur runs onBlur sync.
      if (
        active instanceof HTMLInputElement &&
        (active.type === 'text' || active.type === 'search' || active.type === '')
      ) {
        active.blur();
      }

      event.preventDefault();
      event.stopPropagation();
      onSubmit();
    };

    const onResize = (): void => {
      const node = panelRef.current;
      const current = boxRef.current;

      if (!node || !current) {
        return;
      }

      const next = clampFloatBox(current, node.offsetWidth, node.offsetHeight);
      boxRef.current = next;
      setBox(next);
    };

    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    panelRef.current?.focus({ preventScroll: true });

    return () => {
      openIds.delete(id);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
    };
  }, [id, onDismiss, onSubmit]);

  const moveTo = (clientX: number, clientY: number): void => {
    const node = panelRef.current;

    if (!node) {
      return;
    }

    dragged.current = true;
    const next = clampFloatBox(
      {
        left: clientX - dragOffset.current.x,
        top: clientY - dragOffset.current.y,
      },
      node.offsetWidth,
      node.offsetHeight,
    );
    boxRef.current = next;
    setBox(next);
  };

  return (
    <div
      ref={panelRef}
      class={`ui-float ui-panel ui-chrome${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      tabindex={-1}
      style={
        box
          ? { left: `${box.left}px`, top: `${box.top}px` }
          : { visibility: 'hidden', left: '0px', top: '0px' }
      }
    >
      <div
        class={`ui-float-header${dragging ? ' is-dragging' : ''}`}
        onPointerDown={(event) => {
          if (event.button !== 0) {
            return;
          }

          if (event.target instanceof Element && event.target.closest('button')) {
            return;
          }

          const node = panelRef.current;

          if (!node) {
            return;
          }

          const rect = node.getBoundingClientRect();
          dragOffset.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
          dragged.current = false;
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
        }}
        onPointerMove={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
            return;
          }

          moveTo(event.clientX, event.clientY);
        }}
        onPointerUp={(event) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
            return;
          }

          event.currentTarget.releasePointerCapture(event.pointerId);
          setDragging(false);

          if (dragged.current && boxRef.current) {
            positions.set(id, boxRef.current);
          }
        }}
      >
        <h2 class="ui-float-title">{title}</h2>
        <CloseButton onClick={onDismiss} />
      </div>
      <div class="ui-float-body">{children}</div>
      {footer ? <div class="ui-float-actions">{footer}</div> : null}
    </div>
  );
}
